import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, Clock, Users, Check, ArrowLeft } from "lucide-react";
import { api, inr, fmtDate } from "../lib/api";
import { Footer } from "../components/Footer";

export const EventDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.event(slug).then(setEvent).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="max-w-7xl mx-auto px-6 py-40 text-center" data-testid="event-not-found">
        <p className="font-serif text-4xl text-ink mb-4">This table has wrapped up.</p>
        <Link to="/events" className="text-terracotta uppercase tracking-[0.15em] text-xs">Browse other experiences</Link>
      </div>
    );

  if (!event) return <div className="max-w-7xl mx-auto px-6 py-40 text-muted-earth">Loading...</div>;

  return (
    <div data-testid="event-detail-page">
      {/* Hero image */}
      <motion.div
        initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="relative h-[52vh] md:h-[62vh] overflow-hidden border-b border-ink/10"
      >
        <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-10">
            <span className="bg-ochre text-ink text-[10px] uppercase tracking-[0.15em] px-3 py-1.5">{event.category}</span>
            <h1 className="font-serif font-light text-4xl sm:text-6xl md:text-7xl text-cream tracking-tighter mt-4 leading-none">{event.title}</h1>
          </div>
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <button onClick={() => navigate(-1)} data-testid="back-button" className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-earth hover:text-terracotta transition-colors mb-10">
          <ArrowLeft size={14} /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left */}
          <div className="lg:col-span-8">
            <div className="flex flex-wrap gap-x-8 gap-y-3 text-muted-earth text-xs uppercase tracking-[0.12em] pb-8 border-b border-ink/10">
              <span className="flex items-center gap-2"><Calendar size={15} /> {fmtDate(event.date)}</span>
              <span className="flex items-center gap-2"><Clock size={15} /> {event.time}</span>
              <span className="flex items-center gap-2"><MapPin size={15} /> {event.venue}, {event.city}</span>
              <span className="flex items-center gap-2"><Users size={15} /> {event.spots_left} of {event.spots_total} spots left</span>
            </div>

            <p className="font-serif text-2xl md:text-3xl text-ink font-light leading-relaxed mt-10">{event.description}</p>

            <h3 className="font-serif text-3xl text-ink mt-14 mb-6">What to expect</h3>
            <ul className="space-y-4">
              {event.what_to_expect.map((w, i) => (
                <li key={i} className="flex items-start gap-3 text-muted-earth font-light text-lg">
                  <Check size={20} className="text-terracotta mt-1 shrink-0" /> {w}
                </li>
              ))}
            </ul>
          </div>

          {/* Sticky booking box */}
          <div className="lg:col-span-4">
            <div data-testid="booking-box" className="lg:sticky lg:top-28 border border-ink/15 bg-cream p-7 border-l-2 border-l-terracotta">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-earth">Per ticket</p>
              <p className="font-serif text-5xl text-ink mt-2">{inr(event.price)}</p>

              <div className="mt-7">
                <label className="text-xs uppercase tracking-[0.15em] text-muted-earth">Tickets</label>
                <div className="flex items-center gap-4 mt-3">
                  <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 border border-ink/25 hover:border-terracotta text-xl leading-none">&minus;</button>
                  <span data-testid="qty-value" className="font-serif text-2xl w-8 text-center">{qty}</span>
                  <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(event.spots_left, q + 1))} className="w-10 h-10 border border-ink/25 hover:border-terracotta text-xl leading-none">+</button>
                </div>
              </div>

              <div className="flex items-center justify-between mt-7 pt-6 border-t border-ink/10">
                <span className="text-sm uppercase tracking-[0.12em] text-muted-earth">Total</span>
                <span className="font-serif text-3xl text-ink" data-testid="booking-total">{inr(event.price * qty)}</span>
              </div>

              <button
                data-testid="get-ticket-button"
                onClick={() => navigate(`/checkout/${event.slug}?qty=${qty}`)}
                className="w-full mt-6 bg-terracotta text-cream py-4 uppercase tracking-[0.15em] text-xs hover:bg-terracotta-dark transition-colors"
              >
                Get Ticket
              </button>
              <p className="text-xs text-muted-earth mt-4 text-center leading-relaxed">Your details are shared with the host so nobody arrives a stranger.</p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
